import { Controller } from "@hotwired/stimulus"

// The bell menu: enable browser web push for this browser, and toggle the
// new-message sound. Registration tries an app-wide scope first and falls
// back to the worker's own scope, so a deploy without the
// Service-Worker-Allowed header still subscribes. Every failure is shown in
// the menu and logged, so "it doesn't even ask" is never silent.
export default class extends Controller {
  static targets = ["toggle", "label", "state", "sound"]
  static values = {
    key: String, url: String, worker: String,
    enable: String, disable: String, unsupported: String, denied: String, working: String
  }

  async connect() {
    this.readSound()
    if (!this.supported) {
      this.setState(this.unsupportedValue, { canToggle: false })
      return
    }
    if (!this.keyValue) {
      this.setState("VAPID key missing", { canToggle: false })
      console.error("[chat push] no VAPID public key rendered")
      return
    }
    if (Notification.permission === "denied") {
      this.setState(this.deniedValue, { canToggle: false })
      return
    }
    try {
      const registration = await this.registration()
      const subscription = await registration?.pushManager.getSubscription()
      this.render(Boolean(subscription))
    } catch (error) {
      console.error("[chat push] connect:", error)
    }
  }

  get supported() {
    return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
  }

  // Register the worker for the whole app; fall back to its own scope if the
  // server did not send Service-Worker-Allowed. Wait on the registration's
  // OWN active worker, never navigator.serviceWorker.ready: on the fallback
  // scope that global promise waits for a worker controlling this page and
  // never resolves, which is why prod "never asked" for permission.
  async registration() {
    let reg
    try {
      reg = await navigator.serviceWorker.register(this.workerValue, { scope: "/" })
    } catch (error) {
      console.warn("[chat push] app-wide scope refused, using default scope:", error?.message)
      reg = await navigator.serviceWorker.register(this.workerValue)
    }
    await this.activated(reg)
    return reg
  }

  // Resolve once the registration has an active worker (needed for
  // pushManager.subscribe), waiting on the installing/waiting one if any.
  activated(reg) {
    if (reg.active) return Promise.resolve()
    const worker = reg.installing || reg.waiting
    if (!worker) return Promise.resolve()
    return new Promise((resolve) => {
      worker.addEventListener("statechange", () => {
        if (worker.state === "activated") resolve()
      })
      // Safety net: never block forever.
      setTimeout(resolve, 4000)
    })
  }

  async toggle() {
    if (this.hasStateTarget && this.stateTarget.dataset.canToggle === "false") return
    this.setBusy(true)
    try {
      const registration = await this.registration()
      const existing = await registration.pushManager.getSubscription()
      if (existing) {
        await this.send("DELETE", { endpoint: existing.endpoint })
        await existing.unsubscribe()
        this.render(false)
        return
      }
      const permission = await Notification.requestPermission()
      if (permission !== "granted") {
        this.setState(this.deniedValue, { canToggle: permission === "default" })
        console.warn("[chat push] permission:", permission)
        return
      }
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.applicationServerKey
      })
      const response = await this.send("POST", subscription.toJSON())
      if (!response.ok) throw new Error(`store failed: ${response.status}`)
      this.render(true)
    } catch (error) {
      this.setState(`${error.name || "Errore"}: ${error.message}`, { canToggle: true })
      console.error("[chat push] enable failed:", error)
    } finally {
      this.setBusy(false)
    }
  }

  render(on) {
    if (this.hasToggleTarget) {
      this.toggleTarget.classList.toggle("chat-push--on", on)
      this.toggleTarget.setAttribute("aria-pressed", String(on))
    }
    if (this.hasLabelTarget) this.labelTarget.textContent = on ? this.disableValue : this.enableValue
    if (this.hasStateTarget) this.stateTarget.hidden = true
  }

  setState(text, { canToggle }) {
    if (this.hasStateTarget) {
      this.stateTarget.textContent = text
      this.stateTarget.hidden = false
      this.stateTarget.dataset.canToggle = String(canToggle)
    }
    if (this.hasToggleTarget) this.toggleTarget.disabled = !canToggle
  }

  setBusy(on) {
    if (this.hasLabelTarget && on) this.labelTarget.textContent = this.workingValue
    if (this.hasToggleTarget) this.toggleTarget.disabled = on
  }

  // --- sound toggle (stored per browser) ---------------------------------

  readSound() {
    if (!this.hasSoundTarget) return
    this.soundTarget.checked = localStorage.getItem("chat_sound") !== "off"
  }

  toggleSound() {
    try {
      localStorage.setItem("chat_sound", this.soundTarget.checked ? "on" : "off")
    } catch (_) { /* private mode */ }
    window.dispatchEvent(new CustomEvent("chat:sound-pref", { detail: { on: this.soundTarget.checked } }))
  }

  send(method, body) {
    return fetch(this.urlValue, {
      method,
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.csrfToken },
      body: JSON.stringify(body)
    })
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }

  get applicationServerKey() {
    const padding = "=".repeat((4 - (this.keyValue.length % 4)) % 4)
    const base64 = (this.keyValue + padding).replace(/-/g, "+").replace(/_/g, "/")
    const raw = atob(base64)
    return Uint8Array.from(raw, (c) => c.charCodeAt(0))
  }
}
